import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as PostIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import stockCountService from '../../../../services/stockCountService';
import type { StockCount, StockCountLine } from '../../../../types/venue';
import { formatQty } from '../../../../utils/quantityFormat';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatBottleSize = (ml: number | null): string => {
  if (!ml) return '';
  return ml >= 1000 ? `${ml / 1000}L` : `${ml}ml`;
};

// ── Component ─────────────────────────────────────────────────────────────────

const StockCountReview: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId } = useVenue();
  const { countId } = useParams<{ countId: string }>();
  const entryPath = `/admin/venues/${venueId}/stock-counts/${countId}`;
  const listPath = `/admin/venues/${venueId}/stock-counts`;

  const [count, setCount] = useState<StockCount | null>(null);
  const [lines, setLines] = useState<StockCountLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState<'review' | 'posting' | 'success' | 'error'>('review');
  const [postError, setPostError] = useState<string | null>(null);
  const [movementsPosted, setMovementsPosted] = useState(0);

  const loadCount = useCallback(async () => {
    if (!venueId || !countId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { count: c, lines: l } = await stockCountService.getCount(venueId, countId);
      // Already posted — redirect to read-only entry view
      if (c.status === 'posted') {
        navigate(entryPath, { replace: true });
        return;
      }
      setCount(c);
      setLines(l);
    } catch (err: any) {
      setLoadError(err?.response?.data?.message ?? err?.message ?? 'Failed to load count');
    } finally {
      setLoading(false);
    }
  }, [venueId, countId, entryPath, navigate]);

  useEffect(() => { loadCount(); }, [loadCount]);

  const handlePost = async () => {
    if (!venueId || !countId) return;
    setStep('posting');
    setPostError(null);
    try {
      const result = await stockCountService.postCount(venueId, countId);
      setMovementsPosted(result.movementsPosted);
      setStep('success');
      window.scrollTo(0, 0);
    } catch (err: any) {
      setPostError(err?.response?.data?.message ?? err?.message ?? 'Posting failed');
      setStep('error');
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(entryPath)} sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}>
          Back to Count
        </Button>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  // ── Posting spinner ───────────────────────────────────────────────────────────
  if (step === 'posting') {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, pt: 8 }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">Posting count…</Typography>
      </Box>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <PostIcon sx={{ fontSize: 56, color: '#1a7f37' }} />
          <Typography variant="h5" fontWeight={700}>Count Posted</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {movementsPosted === 0
              ? 'No variances detected — all counted quantities matched expected stock. No stock changes were made.'
              : <>{movementsPosted} variance movement{movementsPosted !== 1 ? 's' : ''} posted to movement history. Stock balances updated where differences were found.</>
            }
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate(entryPath)} sx={{ textTransform: 'none' }}>
            View Posted Count
          </Button>
          <Button variant="contained" onClick={() => navigate(listPath)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Back to Stock Counts
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────────
  if (step === 'error') {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(entryPath)}
          sx={{ textTransform: 'none', color: 'text.secondary', alignSelf: 'flex-start' }}
        >
          Back to Count
        </Button>
        <Alert severity="error">{postError}</Alert>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" onClick={() => navigate(entryPath)} sx={{ textTransform: 'none' }}>
            Back to Count
          </Button>
          <Button variant="contained" onClick={() => setStep('review')} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  if (!count) return null;

  // ── Derived data for review ───────────────────────────────────────────────────
  const countedLines = lines.filter(l => l.countedQty !== null);
  const skippedLines = lines.filter(l => l.countedQty === null);
  const varianceLines = countedLines.filter(l => l.varianceQty !== null && l.varianceQty !== 0);
  const negativeLines = varianceLines.filter(l => (l.varianceQty ?? 0) < 0);
  const missingCountedAt = !count.countedAt;
  const canPost = countedLines.length > 0 && !missingCountedAt;

  // ── Review render ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Nav */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(entryPath)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Back to Count
          </Button>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          <Typography variant="h6" fontWeight={700}>Review & Post Stock Count</Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          startIcon={<PostIcon />}
          onClick={handlePost}
          disabled={!canPost}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Post Stock Count
        </Button>
      </Box>

      {/* Validation warnings */}
      {missingCountedAt && (
        <Alert severity="warning">
          Count date is required before posting. Go back and set the count date.
        </Alert>
      )}
      {countedLines.length === 0 && (
        <Alert severity="warning">
          No lines have been counted yet. Enter at least one quantity before posting.
        </Alert>
      )}

      {/* Immutability notice */}
      <Alert severity="warning">
        <strong>Posting is a one-way action.</strong> For every line with a non-zero variance,
        a stock variance movement will be posted to movement history.
        Zero-variance and uncounted lines are skipped. This cannot be undone.
      </Alert>

      {/* Summary cards */}
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
            Lines Counted
          </Typography>
          <Typography variant="h4" fontWeight={700}>{countedLines.length}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
            Lines Skipped
          </Typography>
          <Typography variant="h4" fontWeight={700} color="text.secondary">{skippedLines.length}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
            With Variance
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={varianceLines.length > 0 ? 'warning.main' : undefined}
          >
            {varianceLines.length}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
            Negative Variance
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            color={negativeLines.length > 0 ? 'error' : undefined}
          >
            {negativeLines.length}
          </Typography>
        </Box>
      </Paper>

      {/* Variance lines table */}
      {varianceLines.length > 0 && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
            <Typography variant="subtitle1" fontWeight={600}>Variance Lines</Typography>
            <Typography variant="caption" color="text.secondary">
              Only lines where counted quantity differs from expected — a stock variance movement will be posted for each
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Item', 'Size', 'Location', 'Expected', 'Counted', 'Variance'].map(h => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {varianceLines.map(line => {
                  const v = line.varianceQty ?? 0;
                  return (
                    <TableRow
                      key={line._id}
                      sx={{ backgroundColor: v < 0 ? alpha('#d32f2f', 0.04) : alpha('#2e7d32', 0.04) }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{line.snapshot.displayName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{formatBottleSize(line.snapshot.bottleSize)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{line.snapshot.locationName ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatQty(line.expectedQty)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{formatQty(line.countedQty ?? 0)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={v < 0 ? 'error' : 'success.main'}
                        >
                          {v > 0 ? '+' : ''}{formatQty(v)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Footer actions */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button onClick={() => navigate(entryPath)} sx={{ textTransform: 'none' }}>
          Back
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<PostIcon />}
          onClick={handlePost}
          disabled={!canPost}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Post Stock Count
        </Button>
      </Box>
    </Box>
  );
};

export default StockCountReview;
